import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

// selectors
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { getAutocompleteFieldsByCountry } from '@domains/documents-review/store/slices/document-review-slice';
import { CountryCode } from '@domains/country';

// hooks
import { useFieldConfidence } from './useFieldConfidence';
import { useSuggestionsAutoCompleteDocs } from './useSuggestionsAutoCompleteDocs';

// utils
import { normalizeAutocompleteValue } from '../utils/dateNormalization';
import { mapFormFieldToSuggestionField, isDisabledSuggestionField } from '../config/fieldConfigs';
import {
  AccessoryAreaFormItem,
  areAccessoryAreasEqual,
} from '../utils/accessoryAreas';

/**
 * Hook que gestiona la lógica de confianza de autocompletado para los campos del formulario.
 * Aplica para Colombia (CO) y México (MX).
 */
export const useAutocompleteConfidence = () => {
  const { watch } = useFormContext();
  const { data: svcItems } = useSuggestionsAutoCompleteDocs();
  const fieldConfidence = useFieldConfidence(svcItems);
  const autoByCountry = useSelector(getAutocompleteFieldsByCountry);
  const country = useSelector(getCountry);

  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());

  // Callback to mark a field as edited
  const handleFieldEdit = (fieldName: string) => {
    setEditedFields(prev => {
      const newSet = new Set(prev);
      newSet.add(fieldName);
      return newSet;
    });
  };

  // Helper to check if user selected a non-default suggestion
  const isNonDefaultSuggestionApplied = (fieldName: string): boolean => {
    // Mapear nombre del formulario a suggestions (ej: halfBaths → halfBathroom)
    const suggestionFieldName = mapFormFieldToSuggestionField(fieldName);
    const fieldItem = svcItems?.find(item => item.field === suggestionFieldName);
    if (!fieldItem) return false;

    // Get the default suggestion value
    const defaultSuggestion = fieldItem.suggestions?.find(s => s.default);
    if (!defaultSuggestion) return false;

    // Get current field value from form
    const currentValue = watch(fieldName);
    if (
      currentValue == null ||
      currentValue === '' ||
      (Array.isArray(currentValue) && currentValue.length === 0)
    ) {
      return false;
    }

    // Normalize both values for comparison
    const normalizedDefault = normalizeAutocompleteValue(
      suggestionFieldName,
      defaultSuggestion.suggestedValue,
      country ?? undefined
    );

    // If current value is different from default, user chose another suggestion
    if (fieldName === 'accessoryAreas') {
      return !areAccessoryAreasEqual(
        currentValue as AccessoryAreaFormItem[],
        normalizedDefault as AccessoryAreaFormItem[],
        country as CountryCode
      );
    }

    return currentValue !== normalizedDefault;
  };

  // Helper to get confidence props for a field
  const getConfidenceProps = (fieldName: string) => {
    // accessoryAreas: sin autocompletado/confianza hasta que backend corrija sugerencias
    if (isDisabledSuggestionField(fieldName, country as CountryCode)) {
      return {};
    }

    const confidence = fieldConfidence[fieldName];

    // Si no hay confidence, no aplicar estilos
    if (!confidence) {
      return {};
    }

    // Para campos HIGH, verificar si ya fueron revisados desde el modal
    if (confidence.level === 'HIGH') {
      // Mapear nombre del formulario a suggestions (ej: halfBaths → halfBathroom)
      const suggestionFieldName = mapFormFieldToSuggestionField(fieldName);
      
      // Verificar si el campo está marcado como reviewed en Redux
      const fieldItem =
        country &&
        autoByCountry?.[country as CountryCode]?.find(
          (item: any) => item.field === suggestionFieldName
        );

      // Si el campo fue revisado desde el modal, no mostrar estilos de confianza
      // (el campo seguirá deshabilitado por useFieldConfidence)
      if (fieldItem?.reviewed) {
        return {};
      }

      // Si no ha sido revisado, mantener los estilos de confianza
      return {
        confidenceLevel: confidence.level,
        confidenceBorderColor: confidence.borderColor,
        confidenceBoxShadow: confidence.boxShadow,
        onFieldEdit: handleFieldEdit,
      };
    }

    // Para campos MEDIUM y LOW, quitar estilos si fueron editados manualmente
    // o si se seleccionó una sugerencia no-default
    if (
      editedFields.has(fieldName) ||
      isNonDefaultSuggestionApplied(fieldName)
    ) {
      return {};
    }

    return {
      confidenceLevel: confidence.level,
      confidenceBorderColor: confidence.borderColor,
      confidenceBoxShadow: confidence.boxShadow,
      onFieldEdit: handleFieldEdit,
    };
  };

  return {
    getConfidenceProps,
    svcItems,
    fieldConfidence,
    editedFields,
  };
};

